
import { computed, defineComponent, inject, onMounted, PropType, reactive, ref, toRefs, watch } from 'vue'
import EditableBlock from '@/components/EditableBlock.vue'
import { CtProduct, CtStockItem } from '@/models/interfaces/content-type'
import ChevronRightIcon from '@/components/Icons/ChevronRightIcon.vue'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import StockInformationEditstate from '@/pages/Products/ProductDetail/components/StockInformationEditstate.vue'
import useContentModule from '@/pages/composables/useContentModule'
import { QSpinnerTail, useQuasar } from 'quasar'
import { Entry, generateUUID } from '@Protium/vue-ozone-utils'
import { useStore } from 'vuex'
import { ContentType, DirectrEvent } from '@/services/directr'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import draggable from 'vuedraggable'
import DButton from '@/components/base/DButton.vue'
import TickIcon from '@/components/Icons/TickIcon.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import LabelBox from '@/pages/Products/components/LabelBox.vue'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'
import ListHeader from '@/components/ListHeader.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import LockClosedIcon from '@/components/Icons/LockClosedIcon.vue'

interface CtStockItemWithCount extends CtStockItem {
  count?: number
}

export default defineComponent({
  name: 'StockInformation',
  components: {
    LockClosedIcon,
    LabelBox,
    AddIcon,
    ListHeader,
    CloseIcon,
    TickIcon,
    DButton,
    TrashIcon,
    StockInformationEditstate,
    EditableBlockInput,
    ChevronRightIcon,
    EditableBlock,
    draggable
  },
  props: {
    components: {
      type: Array as PropType<Array<CtStockItem>>,
      required: true
    },
    productId: {
      type: String,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false
    },
    propValues: {
      type: Object as PropType<{ [key: string]: Array<string> }>,
      default: () => ({})
    },
  },
  emits: ['update:component', 'update:remove-component', 'update:add-components', 'save-order'],

  setup (props, { emit }) {
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']
    const $q = useQuasar()

    const componentDraft = (title) => ({
      sys: {
        id: generateUUID(),
        type: 'stock',
        draft: true,
      },
      sku: undefined,
      ean: [],
      title: title,
      categories: [],
      props: {},
      packing: [],
      images: [],
      brand: undefined,
      tax_tier: undefined,
      vat: undefined,
      weight: 1,
      type: undefined,
      meta: undefined,
      price: undefined,
      batch: undefined,
      producer: undefined,
      dimensions: { x: '1', y: '1', z: '1' },
      volume: undefined,
      supply_chain_tracking: false,
      minimum_order_quantity: undefined
    })

    const state = reactive({
      active: '',
      editingValue: false,
      creatingNewStockItem: false,
      showSearch: false,
      drag: false,
      editingOrder: false,
      editState: [] as Array<CtStockItemWithCount>,
      dragOptions: {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      },
      newStockItemTitle: ''
    })

    const activeComponent = computed(() => {
      return state.editState?.find(component => component.sys?.id === state.active)
    })

    const emitUpdate = (product, productId, event) => {
      emit('update:component', productId, event)
    }

    const isColor = (strColor) => {
      const s = new Option().style
      s.color = strColor
      return s.color !== ''
    }

    const removeComponents = (component) => {
      $q.dialog({
        title: `Are you sure you want to unlink ${component.title}?`,
        cancel: true,
        persistent: false
      }).onOk(() => {
        emit('update:remove-component', component.sys.id)
      })
    }


    const setActiveComponent = (componentId?) => {
      if (state.editingValue) {
        $q.dialog({
          title: 'Unsaved changes will be lost',
          message: 'Are you sure?',
          cancel: true,
          persistent: false
        }).onOk(() => {
          state.editingValue = false
          state.active = componentId
        })
      } else {
        state.editingValue = false
        state.active = componentId
      }
    }

    const setEditState = () => {
      state.editState = (props.components as CtStockItem[] ?? []).reduce((acc, curr) => {
        const existingProduct = acc.find((p) => p.sys.id === curr.sys.id)
        if (existingProduct?.count) {
          existingProduct.count += 1
        } else {
          acc.push({ ...curr, count: 1 })
        }
        return acc
      }, [] as CtStockItemWithCount[])
    }

    watch(() => props.components, () => setEditState(), { deep: true })

    const editing = (event) => {
      state.editingValue = event
    }

    onMounted(() => {
      if (props?.components?.length) {
        state.active = props?.components[0]?.sys?.id
      }
      void setEditState()
    })

    onMounted(() => {
      $directr.listen(DirectrEvent['Update/Stock'], (event) => {
        if (!event?.payload) return
        const components = [...(props.components || [])]
        const index = components?.findIndex(p => p.sys.id === event.payload?.sys?.id)
        if (index < 0) return
        else if (event.payload?.sys?.id
          && components[index].sys.updated_at
          && (new Date(event.payload.sys.updated_at).valueOf() > new Date(components[index].sys.updated_at as string).valueOf())
        ) components.splice(index, 1, {...components[index],...event.payload})
      })
    })

    const startDrag = () => {
      state.drag = true
      state.editingOrder = true
    }

    const revertOrder = () => {
      if (!state.editState) return
      state.editState.splice(0, state.editState.length, ...props.components)
      state.editingOrder = false
    }

    const saveOrder = () => {
      if (!state.editState) return
      const savePayload = { components: state.editState.map(obj => obj.sys.id) }
      $q.notify({
        message: 'Stock order has been updated',
        position: 'top',
        classes: 'custom-notify'
      })
      state.editingOrder = false
      emit('save-order', savePayload)
    }

    const {
      items,
      model,
      mutate,
      setActive,
      requestHandler
    } = useContentModule<CtStockItem>('StockItems', $store)

    const addComponentDialog = reactive({
      show: false,
      selection: [] as UUID[],
      select: (item: Entry) => {
        const index = addComponentDialog.selection.findIndex(s => s === item?.sys?.id)
        if (index >= 0) {
          addComponentDialog.selection.splice(index, 1)
        } else {
          addComponentDialog.selection.push(item.sys?.id)
        }
      },
      model,
      requestHandler,
      addComponent: () => {
        $q.loading.show({ spinner: QSpinnerTail })
        if (!state.newStockItemTitle.length) return

        const draft = componentDraft(state.newStockItemTitle)
        requestHandler.put(ContentType.StockItem, draft.sys.id, draft).then((result) => {
          emit('update:add-components', [result.items[0].sys?.id])
          state.newStockItemTitle = ''
          state.creatingNewStockItem = false
          $q.loading.hide()
          $q.notify({ type: 'positive', message: 'Stock item created', classes: 'custom-notify success' })
        })
      },
      addSelection: () => {
        emit('update:add-components',
          addComponentDialog.selection.filter(s =>
            !props.components.find(c => (c.sys.id === s) || (c.sys.id === (s as any).sys?.id))))
        addComponentDialog.selection = []
        addComponentDialog.show = false
      },
    })


    return {
      ...toRefs(state),
      setActiveComponent,
      setActive,
      activeComponent,
      emitUpdate,
      requestHandler,
      model,
      mutate,
      items,
      removeComponents,
      isColor,
      startDrag,
      revertOrder,
      saveOrder,
      addComponentDialog,
    }
  }
})
